/**
 * LetterPreviewPerChild · iter303ak-fix22c — Quote Sent · Task 3 Action 2
 * ────────────────────────────────────────────────────────────────────
 * Parent-Child aware companion to LetterPreview. Fetches child
 * appraisals under the current parent and renders an accordion — one
 * collapsible panel per child (quote_type + variant), each with its own
 * PDF iframe pointing at the child's appraisal_id. This is the correct
 * behaviour under the Parent-Child architecture where each quote
 * type/variant materialises as its own child appraisal with its own
 * quote_items pool.
 *
 * Falls back gracefully when the appraisal has no children (e.g. legacy
 * appraisals pre-fix21) — renders a single PDF iframe using the parent
 * id, exactly like LetterPreview would.
 *
 * action.params:
 *   • letter_key   — status_letters slot key (used for DRAFT badge +
 *                    Studio deep-link)
 *   • letter_label — human-readable header label
 *   • pdf_route    — API path template with {appraisal_id} placeholder
 *                    ({appraisal_id} is filled with each CHILD's id)
 *   • prompt       — operator instruction text
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FileText, ExternalLink, AlertTriangle, ChevronDown, ChevronRight, Layers } from 'lucide-react';
import { TermsPreviewSection } from './TermsPreviewSection';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function LetterPreviewPerChild({ action, capturedData, onNext, onBack, onCancel, canBack }) {
  const params = action.params || {};
  const letterKey = params.letter_key;
  const letterLabel = params.letter_label || 'Letter';
  const pdfRoute = params.pdf_route || '';
  const parentId = capturedData?.appraisal_id;

  const [children, setChildren] = useState([]);
  const [letter, setLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [openPanels, setOpenPanels] = useState({});
  const [pdfBust] = useState(() => Date.now());
  // iter303ak-fix36 · Invoice Letter only — untagged extras warning.
  const [extrasStatus, setExtrasStatus] = useState(null);

  // Fetch letter (draft badge lookup — non-blocking) + children.
  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      // Non-blocking letter fetch (DRAFT badge + Studio deep-link).
      if (letterKey) {
        api.get(`/app-settings/status-letters/${letterKey}`)
          .then(({ data }) => setLetter(data))
          .catch(() => setLetter({ auto_generated: true }));
      }
      if (!parentId) { setChildren([]); return; }
      // Children fetch. If this appraisal is already a child, the
      // endpoint returns siblings; if it's a parent, returns its
      // children. If nothing, we fall back to single-render below.
      try {
        const { data } = await api.get(`/appraisals/${parentId}/children`);
        setChildren(Array.isArray(data?.children) ? data.children : []);
      } catch { setChildren([]); }
      // Invoice-only: pull extras-tag status so we can surface an amber
      // banner nudging the operator back to T20 Contractor Payment.
      if (letterKey === 'invoice_letter') {
        api.get(`/appraisals/${parentId}/invoice-extras-status`)
          .then(({ data }) => setExtrasStatus(data))
          .catch(() => setExtrasStatus(null));
      }
    } finally { setLoading(false); }
  }, [parentId, letterKey]);

  useEffect(() => { load(); }, [load]);

  // Auto-expand the first panel so the operator sees a PDF immediately.
  useEffect(() => {
    if (children.length > 0 && Object.keys(openPanels).length === 0) {
      setOpenPanels({ [children[0].id || children[0]._id]: true });
    }
  }, [children, openPanels]);

  const isDraft = !!letter?.is_draft;

  const buildPdfUrl = (appraisalId) => {
    if (!pdfRoute || !appraisalId) return '';
    const base = process.env.REACT_APP_BACKEND_URL;
    const tok = typeof window !== 'undefined' ? localStorage.getItem('access_token') : '';
    // iter303ak-fix58 · Force inline disposition so both the iframe and
    // the "New tab" link render the PDF in-browser instead of triggering
    // a download. Backend routes read `?inline=1` to swap the
    // Content-Disposition header.
    const q = `_bust=${pdfBust}&inline=1`;
    return `${base}/api${pdfRoute.replace('{appraisal_id}', appraisalId)}?${q}`;
  };

  // Studio deep-link (same mapping as LetterPreview).
  const openInStudio = () => {
    if (!letterKey) return;
    let variant, active_class;
    if (letterKey === 'quotation_letter' || letterKey === 'invoice_letter') {
      variant = letterKey; active_class = 'quote_letters';
    } else {
      variant = `status_${letterKey}`; active_class = 'status_letters';
    }
    try {
      localStorage.setItem('script-audit-last-nav', JSON.stringify({ variant, active_class }));
    } catch { /* non-blocking */ }
    window.open('/script-audit', '_blank', 'noopener,noreferrer');
  };

  const toggle = (id) => setOpenPanels(prev => ({ ...prev, [id]: !prev[id] }));

  // If no children exist, fall back to single-preview using the parent
  // id (legacy path). Message the operator so they know why.
  const fallbackSingle = !loading && children.length === 0 && parentId;
  const fallbackUrl = fallbackSingle ? buildPdfUrl(parentId) : '';

  return (
    <div data-testid="wf-letter-preview-per-child">
      <div className="bg-gradient-to-br from-emerald-50 to-white border-l-4 border-emerald-400 rounded-r-lg p-5 mb-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-600 font-semibold mb-2">
          <FileText size={12} /> {letterLabel}
          {isDraft && (
            <span data-testid="wf-letter-draft-badge"
              className="normal-case text-[10px] font-semibold text-amber-800 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5 ml-1 inline-flex items-center gap-1">
              <AlertTriangle size={10} /> DRAFT · needs audit
            </span>
          )}
          {children.length > 0 && (
            <span
              data-testid="wf-letter-per-child-count"
              className="normal-case text-[10px] font-semibold text-emerald-800 bg-emerald-100 border border-emerald-300 rounded px-1.5 py-0.5 ml-1 inline-flex items-center gap-1">
              <Layers size={10} /> {children.length} quote{children.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <div className="text-slate-700 text-sm">{params.prompt || `Review each Quotation Letter (one per quote type / variant). Content is live-linked to Script Audit Studio.`}</div>
      </div>

      {err && <div className="mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">{err}</div>}

      {/* iter303ak-fix36 · Invoice-only untagged extras warning. */}
      {extrasStatus && extrasStatus.untagged_count > 0 && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded"
          data-testid="wf-invoice-untagged-extras-banner">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1 text-xs text-amber-900">
              <div className="font-semibold text-sm mb-1">
                {extrasStatus.untagged_count} extra{extrasStatus.untagged_count === 1 ? '' : 's'} not tagged to a child
                {' '}(<span className="font-mono">${extrasStatus.untagged_total.toFixed(2)}</span> total)
              </div>
              <div className="mb-1">
                These extras aren't attached to any accepted child, so they <b>won't appear on any invoice below</b>.
                Head back to <b>T20 Contractor Payment</b> and tag each extra to the correct child before sending invoices.
              </div>
              <ul className="list-disc pl-4 mt-1">
                {extrasStatus.untagged.slice(0, 6).map(u => (
                  <li key={u.id}><b>{u.label}</b> — ${u.cost.toFixed(2)}{u.date ? ` · ${u.date}` : ''}</li>
                ))}
                {extrasStatus.untagged.length > 6 && (
                  <li className="italic">…and {extrasStatus.untagged.length - 6} more</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="p-6 text-sm text-slate-400 italic">Loading quote package…</div>
      )}

      {/* Fallback: no children — render single PDF from the parent id */}
      {fallbackSingle && (
        <>
          <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800" data-testid="wf-letter-per-child-fallback">
            <b>Single-quote mode.</b> This appraisal has no child quote records yet (pre-Parent-Child architecture). Showing one letter for the whole appraisal.
          </div>
          <div className="mb-5 border border-slate-200 rounded-lg bg-slate-50 overflow-hidden">
            <iframe
              src={fallbackUrl}
              title={`${letterLabel} preview`}
              data-testid="wf-letter-pdf-iframe-fallback"
              className="w-full bg-white"
              style={{ height: '560px', border: 'none' }}
            />
          </div>
        </>
      )}

      {/* Accordion — one panel per child */}
      {children.length > 0 && (
        <div className="mb-5 space-y-2" data-testid="wf-letter-per-child-accordion">
          {children.map((c) => {
            const cid = c.id || c._id;
            const isOpen = !!openPanels[cid];
            const label = [c.quote_type_ref, c.variant_label].filter(Boolean).join(' · ');
            const apNo = c.appraisal_no;
            const pdfUrl = buildPdfUrl(cid);
            return (
              <div key={cid} className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid={`wf-letter-per-child-panel-${cid}`}>
                <button
                  onClick={() => toggle(cid)}
                  className={`w-full px-4 py-3 flex items-center gap-3 text-left transition ${isOpen ? 'bg-emerald-50' : 'bg-slate-50 hover:bg-slate-100'}`}
                  data-testid={`wf-letter-per-child-toggle-${cid}`}
                >
                  {isOpen ? <ChevronDown className="w-4 h-4 text-emerald-700 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                      {label || 'Quote'}
                      {apNo && (
                        <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">
                          A{apNo}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {isOpen ? 'Previewing PDF below · content live-linked to Studio' : 'Click to preview'}
                    </div>
                  </div>
                  {pdfUrl && (
                    <a
                      href={pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`wf-letter-per-child-open-tab-${cid}`}
                      className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 text-[11px] bg-white border border-sky-300 text-sky-700 rounded hover:bg-sky-50"
                      title="Open this quotation in a new tab"
                    >
                      <ExternalLink size={11} /> New tab
                    </a>
                  )}
                </button>
                {isOpen && pdfUrl && (
                  <div className="border-t border-slate-200 bg-slate-50">
                    <iframe
                      src={pdfUrl}
                      title={`${letterLabel} — ${label}`}
                      data-testid={`wf-letter-per-child-iframe-${cid}`}
                      className="w-full bg-white"
                      style={{ height: '560px', border: 'none' }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && parentId && pdfRoute.includes('quotation-detailed') && (
        <TermsPreviewSection api={api} jobId={parentId} quotes={children.map((c) => ({ id: c.id || c._id, appraisal_no: c.appraisal_no }))} pdfBust={pdfBust} />
      )}

      <div className="flex items-center gap-3">
        {canBack && (
          <button onClick={onBack} data-testid="wf-back-btn"
            className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>
        )}
        {onCancel && (
          <button onClick={onCancel} data-testid="wf-cancel-btn"
            className="px-4 py-2 text-rose-500 hover:text-rose-700 text-sm">Cancel</button>
        )}
        <span className="flex-1" />
        {letterKey && (
          <button
            onClick={openInStudio}
            data-testid="wf-letter-per-child-edit-in-studio"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border-2 border-indigo-500 text-indigo-700 font-semibold rounded hover:bg-indigo-50"
            title="Open Script Audit Studio to edit this letter template — changes propagate to all quote variants">
            <ExternalLink size={14} /> Edit template in Studio
          </button>
        )}
        <button
          onClick={onNext}
          data-testid="wf-letter-per-child-continue"
          className="px-6 py-2 bg-emerald-600 text-white font-semibold rounded hover:bg-emerald-700">
          Looks good — Continue →
        </button>
      </div>
    </div>
  );
}
